import { createContext, use, type ReactNode } from "react";
import { DEFAULT_LOCALE, localePath, type Locale } from "./locales";
import { en, type Messages } from "./messages.en";
import { nl } from "./messages.nl";
import { th } from "./messages.th";

const CATALOGUES: Record<Locale, Messages> = { en, nl, th };

export function messagesFor(locale: Locale): Messages {
  return CATALOGUES[locale];
}

/**
 * Which language the page around the content is written in.
 *
 * It is a context rather than a hook over the URL because the same components
 * are rendered three ways: by the router in the browser, by the build script
 * into static documents, and by tests with no router at all. All three can hand
 * down a locale; only one of them has a `window.location` to read.
 */
const LocaleContext = createContext<Locale>(DEFAULT_LOCALE);

export function LocaleProvider({
  locale,
  children,
}: {
  locale: Locale;
  children: ReactNode;
}) {
  return <LocaleContext value={locale}>{children}</LocaleContext>;
}

export function useLocale(): Locale {
  return use(LocaleContext);
}

export function useMessages(): Messages {
  return messagesFor(use(LocaleContext));
}

/**
 * An English path, in the language this page is being read in.
 *
 * Every link in the app is written once, against the English route, and passed
 * through here. That is what stops a Dutch page linking a reader back into
 * English halfway through their visit.
 */
export function useLocalePath(): (path: string) => string {
  const locale = use(LocaleContext);
  return (path: string) => localePath(locale, path);
}
